// SAM-199 — front-desk SERVICE booking (selection half only).
//
// Pure helpers for picking a bookable service, its date/slot, and booking-option
// quantities. Kept dependency-free (no React, no API calls) so the slot filtering,
// date grouping and quantity validation logic can be unit tested directly — see
// serviceBooking.test.ts.
//
// IMPORTANT: nothing here computes a price. Booking totals are recomputed
// server-side from `service.booking_options` (functions/booking/pricing.py) — the
// admin only ever sends quantities. See CLAUDE.md "Never send a price from the
// client."

import { isServiceActive } from '../../../hooks/useServices';
import type { Service, ServiceScheduleData, ServiceScheduleDay, ServiceScheduleSlot, BookingOption, ServiceAddOn } from '../../../hooks/useServices';

// ─── Service eligibility ─────────────────────────────────────────────────────

/**
 * A service is offerable to a front-desk operator only if it is active AND has
 * actually been synced to the booking engine — `booking_engine_service_id` is the
 * one field the app already uses for "synced" (see src/pages/Services.tsx's
 * "Synced"/"Not synced" badge). A service with no engine mapping has no real
 * capacity/schedule behind it yet, so it must not show up here even if it is active.
 *
 * "Active" is `isServiceActive()`, the repo's one answer to that question: it prefers
 * `is_active` (the flag a business actually toggles, SAM-569) and only falls back to the legacy
 * `status`/`status_id` when the response omits it. This used to test `status === 'active'`
 * directly, which silently hid every service whose response carries `is_active` and `status_id`
 * but no legacy `status` string — a service created through the API rather than the old wizard
 * has exactly that shape, so the picker came up empty while the catalog showed the services as
 * active.
 */
export type BookableServiceFields = Pick<Service, 'status' | 'is_active' | 'booking_engine_service_id'> & { status_id?: number };

export function isServiceBookable(service: BookableServiceFields): boolean {
  return isServiceActive(service) && !!service.booking_engine_service_id;
}

export function filterBookableServices<T extends BookableServiceFields>(
  services: T[] | undefined | null
): T[] {
  if (!Array.isArray(services)) return [];
  return services.filter(isServiceBookable);
}

/**
 * SAM-1171 — a pass_fee service (SAM-1184) prices a paid pass and is not in the booking engine, so it is offered
 * when it is active, synced or not. New pass sells it with a payment link: no slot, no card in the admin.
 */
export type PassFeeServiceFields = Pick<Service, 'status' | 'is_active' | 'offer_type'> & { status_id?: number };

export function isPassFeeService(service: PassFeeServiceFields | null | undefined): boolean {
  return !!service && String(service.offer_type ?? '').trim().toLowerCase() === 'pass_fee';
}

/** What New pass offers for a Client Booking: active pass_fee services first, then the bookable ones. */
export function filterOfferableServices<T extends BookableServiceFields & PassFeeServiceFields>(
  services: T[] | undefined | null
): T[] {
  if (!Array.isArray(services)) return [];
  const passFees = services.filter((s) => isPassFeeService(s) && isServiceActive(s));
  return [...passFees, ...services.filter((s) => !isPassFeeService(s) && isServiceBookable(s))];
}

/**
 * A 'day' service (cabanas, Maralia — see day_schedule.py) has exactly one
 * whole-day slot per date; there is no time-of-day to pick. `type` is nullable
 * on older rows — same default ('fixed') day_schedule.py's own
 * normalize_service_type falls back to.
 */
export function isDayService(service: Pick<Service, 'type'> | undefined | null): boolean {
  return service?.type === 'day';
}

// ─── Date + slot picking ─────────────────────────────────────────────────────

export interface DateOption {
  date: string; // "YYYY-MM-DD" key into ServiceScheduleData.availability
  day: ServiceScheduleDay;
  availableCount: number;
}

/**
 * A 'day' service's schedule entry (day_schedule.py::build_day_schedule_entries)
 * has no `slots` array — it describes the whole date directly via `available`/
 * `opens`/`closes`. A 'fixed'/'flexible' entry always has `slots` (possibly
 * empty). Check the SHAPE rather than trusting `service.type`, since the two
 * must agree for this to matter and the schedule payload is the one that
 * actually decides what UI applies.
 */
export function isDayScheduleDay(day: ServiceScheduleDay | undefined | null): boolean {
  return !!day && !Array.isArray(day.slots);
}

/** Whole-day time value sent for a 'day' service booking — see buildReservationWindow's day_schedule.py note. */
export const DAY_SERVICE_TIME = '00:00';

/** A slot is pickable when it isn't explicitly marked unavailable and isn't full. */
export function isSlotAvailable(slot: ServiceScheduleSlot): boolean {
  if (slot.available === false) return false;
  return slot.bookings_count < slot.max_bookings;
}

export function filterAvailableSlots(day: ServiceScheduleDay | undefined | null): ServiceScheduleSlot[] {
  if (!day?.slots) return [];
  return day.slots.filter(isSlotAvailable);
}

export function remainingCapacity(slot: ServiceScheduleSlot): number {
  return Math.max(0, slot.max_bookings - slot.bookings_count);
}

/**
 * Whether a single schedule-day entry (either shape) is bookable at all: a
 * 'fixed'/'flexible' day needs at least one available slot; a 'day' entry is
 * bookable when `available` is true (its own remaining_capacity, if any, is
 * already folded into that flag server-side).
 */
export function isDayBookable(day: ServiceScheduleDay | undefined | null): boolean {
  if (!day) return false;
  if (isDayScheduleDay(day)) return day.available === true;
  return filterAvailableSlots(day).length > 0;
}

/**
 * Every date in `schedule.availability` that is bookable, sorted
 * chronologically ("YYYY-MM-DD" keys sort correctly as strings). Handles both
 * schedule shapes (see isDayScheduleDay) — a 'day' entry counts as 1 "slot"
 * when available, a slot-based entry counts its actual available slots.
 */
export function getBookableDates(schedule: ServiceScheduleData | undefined | null): DateOption[] {
  if (!schedule?.availability) return [];
  return Object.entries(schedule.availability)
    .map(([date, day]) => ({
      date,
      day,
      availableCount: isDayScheduleDay(day) ? (day.available ? 1 : 0) : filterAvailableSlots(day).length,
    }))
    .filter((entry) => entry.availableCount > 0)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/** True when the backend served this schedule from the local fallback (SAM-194 follow-up), not the engine. */
export function isLocalFallbackSchedule(schedule: ServiceScheduleData | undefined | null): boolean {
  return (schedule as any)?.schedule_source === 'local';
}

// ─── Booking-option quantities (never a price) ──────────────────────────────

export interface QuantitySelection {
  optionId: string;
  quantity: number;
  // SAM-624 — the rate group (SAM-588) this line is bought in. Present only for a service with
  // active rate groups and an option that has group prices; absent otherwise.
  rateGroup?: string;
}

/** Starting quantities: everything at its option's own minimum (0 when unset). */
export function initialQuantities(options: BookingOption[] | undefined | null): Record<string, number> {
  const quantities: Record<string, number> = {};
  (options || []).forEach((opt) => {
    quantities[opt.id] = opt.min_amount && opt.min_amount > 0 ? opt.min_amount : 0;
  });
  return quantities;
}

export function clampQuantity(option: BookingOption, quantity: number): number {
  const min = option.min_amount ?? 0;
  // max_amount === 0 means "no explicit cap" for most SAM booking options — treat it as unbounded.
  const max = option.max_amount && option.max_amount > 0 ? option.max_amount : Number.MAX_SAFE_INTEGER;
  if (!Number.isFinite(quantity)) return min;
  return Math.min(Math.max(Math.round(quantity), min), max);
}

export interface QuantityValidationResult {
  valid: boolean;
  problems: string[];
}

/**
 * At least one unit total, and every option within its own min/max. Options with
 * quantity 0 are fine as long as the overall total is positive — mirrors how the
 * product flow lets adults-only bookings skip a children option entirely.
 */
export function validateQuantities(
  options: BookingOption[] | undefined | null,
  quantities: Record<string, number>
): QuantityValidationResult {
  const problems: string[] = [];
  const opts = options || [];

  opts.forEach((opt) => {
    const qty = quantities[opt.id] ?? 0;
    const min = opt.min_amount ?? 0;
    const max = opt.max_amount && opt.max_amount > 0 ? opt.max_amount : undefined;
    if (qty > 0 && qty < min) {
      problems.push(`${opt.label_en || opt.id}: minimum is ${min}.`);
    }
    if (max !== undefined && qty > max) {
      problems.push(`${opt.label_en || opt.id}: maximum is ${max}.`);
    }
  });

  const total = opts.reduce((sum, opt) => sum + (quantities[opt.id] ?? 0), 0);
  if (opts.length > 0 && total <= 0) {
    problems.push('Select at least one guest.');
  }

  return { valid: problems.length === 0, problems };
}

// ─── Add-ons (SAM-590) — quantities only, never a price ─────────────────────
//
// Same "never send a price" rule as booking-option quantities above: an add-on
// selection is just `{id, qty}` pairs, priced server-side from
// `service.settings.add_ons`.

export interface AddOnSelection {
  id: string;
  qty: number;
}

/** Starting quantities: required add-ons start at 1 (they can't go to 0), everything else at 0. */
export function initialAddOnQuantities(addOns: ServiceAddOn[] | undefined | null): Record<string, number> {
  const quantities: Record<string, number> = {};
  (addOns || []).forEach((addOn) => {
    quantities[addOn.id] = addOn.required ? 1 : 0;
  });
  return quantities;
}

/** 0..max_qty, except a required add-on's floor is 1 (it can't be zeroed out). */
export function clampAddOnQuantity(addOn: ServiceAddOn, quantity: number): number {
  const min = addOn.required ? 1 : 0;
  const max = addOn.max_qty && addOn.max_qty > 0 ? addOn.max_qty : Number.MAX_SAFE_INTEGER;
  if (!Number.isFinite(quantity)) return min;
  return Math.min(Math.max(Math.round(quantity), min), max);
}

// ─── Final selection payload (handed to the not-yet-wired submit callback) ──

export interface ServiceBookingSelection {
  serviceId: string;
  serviceName: string;
  date: string; // "YYYY-MM-DD"
  time: string; // matches ServiceScheduleSlot.time, e.g. "14:00"
  quantities: QuantitySelection[]; // only quantities — never a price
  // SAM-590 — only non-zero add-on quantities, same "quantities only" contract as `quantities`.
  addOns: AddOnSelection[];
  // SAM-624 — the cabana/unit being booked (SAM-591), a `settings.resources[].key`. Present only
  // when the service is unit-priced with more than one resource (the server requires it then).
  resourceKey?: string;
  scheduleSource: 'engine' | 'local' | undefined;
  // SAM-199 submit wiring: the service's own duration (parsed from its ISO-8601
  // `duration`, e.g. "PT60M" -> 60 via parseISODurationToMinutes in useServices.ts),
  // carried through so the submit step can compute a real reservation_end instead of
  // collapsing every booking to a zero-length window. Undefined when the service has
  // no duration set — the submit step falls back to treating it as a zero-length slot,
  // same as the backend's own `reservation_end` default (booking_orchestration.py:
  // `reservation_end = booking_data.get("reservation_end", reservation_start)`).
  durationMinutes?: number;
}

export function buildServiceBookingSelection(params: {
  service: Pick<Service, 'id' | 'name' | 'name_en' | 'name_es'>;
  date: string;
  time: string;
  quantities: Record<string, number>;
  // SAM-590 — optional: a service with no add-ons (or a caller that hasn't wired them yet) sends
  // `addOns: []`, same as `quantities` would for a service with no booking options.
  addOnQuantities?: Record<string, number>;
  // SAM-624 — optionId -> rate group id, ALREADY resolved (picked or defaulted) by the caller;
  // only the entries for lines with quantity > 0 are copied onto the selection.
  rateGroups?: Record<string, string>;
  resourceKey?: string | null;
  scheduleSource?: string;
  durationMinutes?: number;
}): ServiceBookingSelection {
  const quantities: QuantitySelection[] = Object.entries(params.quantities)
    .filter(([, qty]) => qty > 0)
    .map(([optionId, quantity]) => {
      const rateGroup = params.rateGroups?.[optionId];
      return rateGroup ? { optionId, quantity, rateGroup } : { optionId, quantity };
    });

  const addOns: AddOnSelection[] = Object.entries(params.addOnQuantities || {})
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => ({ id, qty }));

  return {
    serviceId: params.service.id,
    serviceName: params.service.name_en || params.service.name || params.service.name_es || '',
    date: params.date,
    time: params.time,
    quantities,
    addOns,
    ...(params.resourceKey ? { resourceKey: params.resourceKey } : {}),
    scheduleSource: params.scheduleSource === 'engine' || params.scheduleSource === 'local'
      ? params.scheduleSource
      : undefined,
    durationMinutes: params.durationMinutes && params.durationMinutes > 0 ? params.durationMinutes : undefined,
  };
}
